import type { FormDefinition, FormField } from "@colab/shared";
import { formatDate } from "@/lib/utils";

// Read-only renderer that walks the snapshotted form definition. Iterates
// sections + fields in definition order so the layout matches what the
// submitter saw at the time of submission — later form-builder edits don't
// shift labels (the snapshot is enforced by the migration CHECK + the
// Zod-validated write path).
//
// SPA port of components/admin/submissions/SubmissionPayload.tsx; only
// difference is the "use client" directive (no client/server split) and
// the import path for FormField.

export function SubmissionPayload({
  definition,
  payload,
}: {
  definition: FormDefinition;
  payload: Record<string, unknown>;
}) {
  return (
    <div className="space-y-5 rounded-md border bg-background p-4">
      {definition.sections.map((section, sIdx) => (
        <div key={section.id ?? sIdx} className="space-y-3">
          {section.heading ? (
            <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {section.heading}
            </h4>
          ) : null}
          {section.fields.map((field) => {
            if (field.kind === "section_heading") {
              return (
                <h4
                  key={field.id}
                  className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
                >
                  {field.label}
                </h4>
              );
            }
            return (
              <PayloadRow
                key={field.id}
                field={field}
                value={payload[field.name]}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

function PayloadRow({ field, value }: { field: FormField; value: unknown }) {
  return (
    <div className="grid grid-cols-[180px_1fr] items-baseline gap-3">
      <dt className="text-xs font-medium text-muted-foreground">
        {field.label}
      </dt>
      <dd className="text-sm text-foreground">{renderValue(field, value)}</dd>
    </div>
  );
}

function renderValue(field: FormField, value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground">—</span>;
  }
  switch (field.kind) {
    case "checkbox":
      return value === true ? "Yes" : "No";
    case "date":
      return typeof value === "string" ? formatDate(value) : String(value);
    case "select": {
      if (field.multiple && Array.isArray(value)) {
        const labels = value.map(
          (v) =>
            field.options.find((o) => o.value === v)?.label ?? String(v),
        );
        return labels.join(", ");
      }
      const opt = field.options.find((o) => o.value === value);
      return opt?.label ?? String(value);
    }
    case "file": {
      if (
        value &&
        typeof value === "object" &&
        "url" in value &&
        "name" in value
      ) {
        const v = value as { url: string; name: string };
        return (
          <a
            href={v.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline"
          >
            {v.name}
          </a>
        );
      }
      return <span className="text-muted-foreground">—</span>;
    }
    case "long_text":
      return <span className="whitespace-pre-wrap">{String(value)}</span>;
    case "number":
      return typeof value === "number"
        ? value.toLocaleString()
        : String(value);
    case "partner_lookup":
      // Stored as either a partner UUID or an embed-context email. Show
      // the raw value — the drawer's header already surfaces the partner
      // name when available.
      return <code className="font-mono text-xs">{String(value)}</code>;
    case "section_heading":
      return null;
    default:
      return String(value);
  }
}
